import styled from '@emotion/styled';
import {
  Avatar,
  Button as _Button,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Modal,
  Paper,
  Popper,
  Typography,
  withStyles,
} from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { createElement, Dispatch, FC, MouseEvent, MouseEventHandler, SetStateAction, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Language from '../../../../entity/Language';
import User from '../../../../entity/User';
import theme from '../../../../theme';
import {
  COLOR_MENU_BACKGROUND,
  COLOR_MENU_HEADER,
  COLOR_SECONDARY_BUTTON_BACKGROUND_MAIN,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_MAIN,
} from '../../../constant/color';
import { SPACING_UNIT } from '../../../constant/spacing';

interface Props {
  signedInUser: User;
  onClickSignOut: MouseEventHandler<HTMLElement>;
  onSubmit: (lang: Language) => void;
  currentLang: Language | null;
  isSmartPhone: boolean;
  className?: string;
  profileWebUrl: string;
}

const UserMenu: FC<Props> = ({
  signedInUser,
  onClickSignOut,
  onSubmit,
  currentLang,
  isSmartPhone,
  className,
  profileWebUrl,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedLang, setSelectedLang] = useState<Language | null>(currentLang);

  const onClickMenu = (e: MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const onRequestClose = () => {
    setOpen(false);
  };

  const onClickSignOutButton = (e: MouseEvent<HTMLElement>) => {
    onClickSignOut(e);
    setOpen(false);
  };

  return (
    <Root className={className}>
      <IconButton onClick={onClickMenu}>
        {!signedInUser!.profileImageUrl ? (
          <AccountCircle style={{ color: COLOR_SECONDARY_BUTTON_BACKGROUND_MAIN, width: 32, height: 32 }} />
        ) : (
          <Avatar alt={signedInUser!.name} src={signedInUser!.profileImageUrl} />
        )}
      </IconButton>

      {isSmartPhone ? (
        <Modal
          open={open}
          onClose={onRequestClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StyledPaper style={{ width: '80%', backgroundColor: COLOR_MENU_BACKGROUND, borderRadius: 8 }}>
            <List
              style={{
                padding: 0,
              }}
            >
              <ListItem
                style={{
                  height: 48,
                  display: 'flex',
                  justifyContent: 'space-between',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.16)',
                  backgroundColor: COLOR_MENU_HEADER,
                  borderRadius: '8px 8px 0px 0px',
                }}
              >
                <Typography
                  variant="body1"
                  style={{
                    position: 'static',
                    fontSize: 16,
                    fontWeight: 700,
                    color: COLOR_TEXT_MAIN,
                  }}
                >
                  <FormattedMessage id="menu.account_menu" />
                </Typography>
                <IconButton onClick={onRequestClose} style={{ width: 28, height: 28 }}>
                  <CloseIcon style={{ color: COLOR_TEXT_MAIN, width: 24, height: 24 }} />
                </IconButton>
              </ListItem>
              <List
                style={{
                  padding: '16px 0px 24px 0px',
                }}
              >
                {LanguageMenu(selectedLang, onSubmit, setSelectedLang)}
                {ProfileMenu(profileWebUrl)}
                {LogoutMenu(onClickSignOutButton)}
              </List>
            </List>
          </StyledPaper>
        </Modal>
      ) : (
        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" transition disablePortal>
          <ClickAwayListener onClickAway={onRequestClose}>
            <StyledPaper style={{ width: 320, backgroundColor: COLOR_MENU_BACKGROUND }}>
              <List
                style={{
                  padding: 0,
                }}
              >
                <ListItem
                  style={{
                    height: 48,
                    display: 'flex',
                    justifyContent: 'space-between',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.16)',
                    backgroundColor: COLOR_MENU_HEADER,
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{
                      position: 'static',
                      fontSize: 16,
                      fontWeight: 700,
                      color: COLOR_TEXT_MAIN,
                    }}
                  >
                    <FormattedMessage id="menu.account_menu" />
                  </Typography>
                  <IconButton onClick={onRequestClose} style={{ width: 28, height: 28 }}>
                    <CloseIcon style={{ color: COLOR_TEXT_MAIN, width: 24, height: 24 }} />
                  </IconButton>
                </ListItem>
                <List
                  style={{
                    padding: '16px 0px 24px 0px',
                  }}
                >
                  {LanguageMenu(selectedLang, onSubmit, setSelectedLang)}
                  {ProfileMenu(profileWebUrl)}
                  {LogoutMenu(onClickSignOutButton)}
                </List>
              </List>
            </StyledPaper>
          </ClickAwayListener>
        </Popper>
      )}

      {/* <StyledMenu
          id="menu-appbar"
          anchorEl={this.state.anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={this.state.open}
          onClose={this.onRequestClose}
        >
          <MenuItem button component="a" href={profileWebUrl} target="_blank" rel="noopener noreferrer">
            <ListItemIcon>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="headbar.profile" />} />
          </MenuItem>

          <MenuItem button onClick={this.onClickSignOut}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>

            <ListItemText primary={<FormattedMessage id="headbar.sign_out" />} />
          </MenuItem>
        </StyledMenu> */}
    </Root>
  );
};

export const MENU_ITEM_HEIGHT = 48;

const LanguageMenu = (
  selectedLang: Language | null,
  onSubmit: (lang: Language) => void,
  setSelectedLang: Dispatch<SetStateAction<Language | null>>,
) => {
  const item = 'language';
  const msgId = `headbar.language`;
  return (
    <ListItem
      key={item}
      style={{
        height: MENU_ITEM_HEIGHT,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 'none',
        // padding: '16px 16px 16px 32px',
        margin: '0px 0px',
      }}
    >
      <Typography style={typographyStyle.popupMenu}>
        <FormattedMessage id={msgId} />
      </Typography>
      <Button
        size="small"
        style={
          selectedLang === 'EN'
            ? { color: COLOR_TEXT_MAIN, fontWeight: 700 }
            : { color: COLOR_TEXT_DISABLED, fontWeight: 400 }
        }
        onClick={() => {
          onSubmit('EN');
          setSelectedLang('EN');
        }}
      >
        En
      </Button>
      <Typography style={{ color: COLOR_TEXT_DISABLED, margin: '0px 4px' }}>/</Typography>
      <Button
        style={
          selectedLang === 'JA'
            ? { width: 24, color: COLOR_TEXT_MAIN, fontWeight: 700 }
            : { color: COLOR_TEXT_DISABLED, fontWeight: 400 }
        }
        onClick={() => {
          onSubmit('JA');
          setSelectedLang('JA');
        }}
      >
        Ja
      </Button>
    </ListItem>
  );
};

const LogoutMenu = (onClickSignOutButton: (e: MouseEvent<HTMLElement>) => void) => {
  const item = 'logout';
  const msgId = `headbar.sign_out`;
  return (
    <ListItem
      button
      key={item}
      onClick={onClickSignOutButton}
      style={{
        height: MENU_ITEM_HEIGHT,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 'none',
        // padding: '16px 16px 16px 32px',
        margin: '0px 0px',
      }}
    >
      <Typography style={typographyStyle.popupMenu}>
        <FormattedMessage id={msgId} />
      </Typography>
    </ListItem>
  );
};

const ProfileMenu = (url: string) => {
  const msgId = `headbar.profile`;
  return (
    <ListItem
      button
      component="a"
      href={url}
      target="_blank"
      key={'profile'}
      rel="noopener noreferrer"
      style={{
        height: MENU_ITEM_HEIGHT,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 'none',
        // padding: '16px 16px 16px 32px',
        margin: '0px 0px',
      }}
    >
      <Typography style={typographyStyle.popupMenu}>
        <FormattedMessage id={msgId} />
      </Typography>
      <ListItemIcon style={nestedStyle.icon}>
        <OpenInNew fontSize="inherit" />
      </ListItemIcon>
    </ListItem>
  );
};

const nestedStyle = {
  root: {
    paddingLeft: 36,
  },
  text: {
    paddingRight: 8,
    flexGrow: 0,
  },
  icon: {
    marginRight: 0,
    fontSize: '20px',
    color: COLOR_TEXT_MAIN,
    justifyContent: 'flex-end',
  },
};

const Root = styled('div')();

const Button = withStyles({
  root: {
    padding: SPACING_UNIT / 2,
    textTransform: 'none',
    minWidth: 'auto',
  },
})(_Button);

const StyledPaper = withStyles({
  rounded: {
    borderRadius: 0,
  },
  elevation1: {
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.16)',
  },
  [theme.breakpoints.up('sm')]: {
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50% -50%)',
  },
})(Paper);

// const StyledModal = withStyles({
//   root: {
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//   }
// })(Modal);

const typographyStyle = {
  popupMenu: {
    /* Font / 14px / W7 / 100% */
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '100%',
    /* identical to box height, or 14px */

    /* Color / Black */
    color: COLOR_TEXT_MAIN,

    /* Inside Auto Layout */
    flex: 'none',
    order: 0,
    flexGrow: 1,
  },
};

// const StyledMenu = withStyles({
//   paper: {
//     backgroundColor: '#88E18E',
//     border: '1px solid #d3d4d5',
//   },
// })((props: MenuProps) => (
//   <Menu
//     elevation={0}
//     getContentAnchorEl={null}
//     anchorOrigin={{
//       vertical: 'bottom',
//       horizontal: 'center',
//     }}
//     transformOrigin={{
//       vertical: 'top',
//       horizontal: 'center',
//     }}
//     {...props}
//   />
// ));

// const StyledMenu = styled(Menu)({
//   marginTop: SPACING_LARGE,
// }) as typeof Menu;

export default UserMenu;
